import { useState, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { showSuccessToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { KeywordGeneratorToolPageModal } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageModal";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { useAllKeywordGroups, useCreateKeywordGroup, useUpdateKeywordGroup } from "app/services/keyword-groups/hooks";
import { keywordGroupsTransforms } from "app/services/keyword-groups/utils/keywordGroupsTransforms";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { generalGroupToastMessage } from "../../TableSelectionGroupKeywordComponent";
import { ETableSelectionNewGroupDropdownMode } from "../../TableSelectionNewGroupDropdown";

import type { ICreateOrUpdateKeywordListPopoverProps } from "./CreateOrUpdateKeywordListPopover";
import { CreateOrUpdateKeywordListPopover } from "./CreateOrUpdateKeywordListPopover";

const KEYWORD_ANALYSIS_STATE = "organicsearch_keywordAnalysis_2";

interface ICreateOrUpdateKeywordListPopoverWrapperProps extends Pick<
    ICreateOrUpdateKeywordListPopoverProps,
    | "selectedRows"
    | "isKeywordRemovalModalOpen"
    | "setIsKeywordRemovalModalOpen"
    | "isKeywordListPopoverOpen"
    | "setIsKeywordListPopoverOpen"
    | "keywordListButtonRef"
    | "isCreateNewListModalOpen"
    | "onRemoveKeywordsFromList"
> {
    keywordGroupState?: string;
    isSearchTrends?: boolean;
    clearAllSelectedRows: () => void;
    setIsCreateNewListModalOpen: (isOpen: boolean) => void;
    /**
     * Override the "Analyze list" redirect after a new group is created. Receives the new group id.
     * When omitted, the wrapper navigates to `keywordGroupState` with `keyword: *${id}` (keyword research convention).
     */
    onViewGroupClick?: (groupId: string) => void;
    /**
     * Override the href used by the existing-group toast deep-link. Receives the group id.
     * When omitted, the wrapper builds the link via `proNavigator.href(toastStateLink ?? keywordGroupState, { country, duration, keyword: \`*${id}\` })`.
     */
    getToastHref?: (groupId: string) => string;
    /**
     * State name used to build the toast deep-link when keywords are added to an existing group, when `getToastHref` is not provided.
     * Defaults to the keyword research analysis state.
     */
    toastStateLink?: string;
}

export const CreateOrUpdateKeywordListPopoverWrapper = (props: ICreateOrUpdateKeywordListPopoverWrapperProps) => {
    const {
        userGroups,
        sharedGroups,
        isLoading: isKeywordGroupsLoading,
        refetch: refetchKeywordGroups,
    } = useAllKeywordGroups();
    const { mutateAsync: createKeywordGroup } = useCreateKeywordGroup();
    const { mutateAsync: updateKeywordGroup } = useUpdateKeywordGroup();

    const keywordGroups = useMemo(
        () => ({
            OwnerGroups: userGroups,
            SharedGroups: sharedGroups,
        }),
        [userGroups, sharedGroups],
    );

    const { params } = useCurrentStateAndParams();
    const { country, duration } = params;
    const dispatch = useDispatch();
    const {
        selectedRows,
        keywordGroupState: keywordGroupStateProp,
        isSearchTrends,
        clearAllSelectedRows,
        isKeywordRemovalModalOpen,
        setIsKeywordRemovalModalOpen,
        isKeywordListPopoverOpen,
        setIsKeywordListPopoverOpen,
        keywordListButtonRef,
        isCreateNewListModalOpen,
        setIsCreateNewListModalOpen,
        onRemoveKeywordsFromList,
        onViewGroupClick: onViewGroupClickProp,
        getToastHref,
        toastStateLink,
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [groupId, setGroupId] = useState("");
    const [newlyCreatedGroup, setNewlyCreatedGroup] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [mode, setMode] = useState(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
    const [isNavigateToGroupModalOpen, setIsNavigateToGroupModalOpen] = useState(false);

    const maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount() as number;
    const isRowLimitExceeded = selectedRows.length > maxKeywordsInGroup;

    const keywordGroupState =
        keywordGroupStateProp ??
        (isSearchTrends
            ? "digitalsuite_marketresearch_keywordmarketanalysis_total"
            : "organicsearch_keywordAnalysis_overview_2");

    const onViewGroupClick = () => {
        if (onViewGroupClickProp) {
            onViewGroupClickProp(groupId);
            return;
        }
        proNavigator.go(keywordGroupState, {
            country,
            duration,
            keyword: `*${groupId}`,
            isTerm: false,
        });
    };

    const onNewListButtonClick = () => {
        setIsKeywordListPopoverOpen(false);
        setIsCreateNewListModalOpen(true);
    };

    const onStartOverClick = () => {
        clearAllSelectedRows();
        setIsNavigateToGroupModalOpen(false);
    };

    const resetState = () => {
        setMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setErrorMessage(null);
    };

    const onCancel = () => {
        if (mode === ETableSelectionNewGroupDropdownMode.NEW_GROUP) {
            allTrackers.dispatchTrackEvent("add to keyword group", "click", "Create New Keyword Group/Cancel");
        }

        resetState();
        setIsCreateNewListModalOpen(false);
    };

    const onSubmitNewList = async (name: string) => {
        const validation = await KeywordGroupEditorHelpers.validateTitle(name);
        if (!validation.isValid) {
            setErrorMessage(validation.errorMessage ?? null);
        } else {
            setIsLoading(true);
            const selectedRowsToSubmit = isRowLimitExceeded ? selectedRows.slice(0, maxKeywordsInGroup) : selectedRows;
            const group = {
                /* eslint-disable @typescript-eslint/naming-convention */
                Name: name,
                Keywords: selectedRowsToSubmit.map((keyword) => keyword.toLowerCase()),
                GroupHash: null,
                Id: null,
                /* eslint-enable @typescript-eslint/naming-convention */
            };
            try {
                const createdGroup = await createKeywordGroup(group);
                if (createdGroup) {
                    // This is a hack for making sure the keyword querybar is updated with the new group before navigating.
                    // The keyword querybar uses the old keyword group service and therefor needs to be manually updated.
                    await keywordGroupsQueryClient.invalidateAll();
                    await refetchKeywordGroups();
                    clearAllSelectedRows();
                    resetState();
                    if (createdGroup.Id) {
                        setGroupId(createdGroup.Id);
                        setNewlyCreatedGroup(true);
                        setIsNavigateToGroupModalOpen(true);
                    }
                    setIsCreateNewListModalOpen(false);
                }
            } catch (error) {
                const customErrorMessage = "Error creating keyword group";
                Sentry.error(customErrorMessage, error as Error);
                setErrorMessage(customErrorMessage);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onNewGroupClick = (item: { text: string }) => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New Keyword List/Save/${item.text}/${selectedRows.length}`,
        );
        if (isRowLimitExceeded) {
            setMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
        } else {
            setMode(ETableSelectionNewGroupDropdownMode.NEW_GROUP);
        }
    };

    const showGroupLinkToast = (id: string, text: string, label: string, stateLink?: string) => {
        const linkToGroup =
            getToastHref?.(id) ??
            proNavigator.href(stateLink ?? keywordGroupState, {
                country,
                duration,
                keyword: `*${id}`,
            });
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text,
                    linkText: label,
                    href: linkToGroup,
                    onClick: allTrackers.dispatchTrackEvent.bind(
                        allTrackers,
                        "add to keyword group",
                        "click",
                        "internal link/keywordAnalysis.overview",
                    ) as () => void,
                }),
            ),
        );
    };

    const updateGroupWithNewKeywords = async (
        group: IUserKeywordGroup,
        existingGroupId: string,
        existingGroupName: string,
        newKeywordsCount: number,
    ) => {
        try {
            setIsLoading(true);
            const updatedGroup = await updateKeywordGroup(group);
            if (updatedGroup) {
                // This is a hack for making sure the keyword querybar is updated with the new group before navigating.
                // The keyword querybar uses the old keyword group service and therefor needs to be manually updated.
                await keywordGroupsQueryClient.invalidateAll();

                setIsKeywordListPopoverOpen(false);
                showGroupLinkToast(
                    existingGroupId,
                    generalGroupToastMessage(existingGroupName, newKeywordsCount),
                    translate("table.selection.keywords.seegroup"),
                    toastStateLink ?? KEYWORD_ANALYSIS_STATE,
                );
                clearAllSelectedRows();
                resetState();
            }
        } catch (error) {
            Sentry.error("Error updating keyword group", error);
        } finally {
            setIsLoading(false);
        }
    };

    const onExistingGroupClick = async (item: { id: string }) => {
        const existingGroup = keywordGroups.OwnerGroups.find((group) => group.Id === item.id);
        if (!existingGroup) {
            return;
        }
        const currentKeywords = existingGroup.Keywords.map((keyword) => keyword.toLowerCase());
        const newKeywords = selectedRows.map((keyword) => keyword.toLowerCase());
        const withoutDuplications = [...new Set([...currentKeywords, ...newKeywords])];
        const newKeywordsCount = withoutDuplications.length - currentKeywords.length;
        // if no new keywords was added
        if (newKeywordsCount === 0) {
            setMode(ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS);
        }
        // new keywords added
        else {
            const group = {
                ...existingGroup,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Keywords: withoutDuplications,
            };
            // check number of keywords in group
            if (group.Keywords.length > maxKeywordsInGroup) {
                setMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
            } else {
                // @ts-ignore FIXME: strict migration - group type mismatch with IUserKeywordGroup — existingGroup spread doesn't satisfy required props
                await updateGroupWithNewKeywords(
                    group,
                    existingGroup.Id ?? "",
                    existingGroup.Name ?? "",
                    newKeywordsCount,
                );
            }
        }
    };

    const onGroupClick = (item: { text: string; id: string }) => {
        // click on new group
        if (!item.id) {
            onNewGroupClick(item);
        }
        // click on existing group
        else {
            void onExistingGroupClick(item);
        }
    };

    if (isKeywordGroupsLoading) {
        return null;
    }

    return (
        <>
            <CreateOrUpdateKeywordListPopover
                mode={mode}
                count={maxKeywordsInGroup}
                allItemsExistsMessage="table.selection.keywords.exists"
                maxItemsMessage="table.selection.keywords.max.keywords"
                groups={keywordGroupsTransforms.toDropDown(keywordGroups?.OwnerGroups)}
                onSubmitNewList={(name: string) => void onSubmitNewList(name)}
                isLoading={isLoading}
                errorMessage={errorMessage ?? ""}
                onGroupClick={onGroupClick}
                onNewListButtonClick={onNewListButtonClick}
                isCreateNewListModalOpen={isCreateNewListModalOpen}
                onCancel={onCancel}
                groupIconName="keyword-group"
                newGroupNameLabel={translate("table.selection.newgroup.keywords.title")}
                selectedRows={selectedRows}
                isKeywordRemovalModalOpen={isKeywordRemovalModalOpen}
                setIsKeywordRemovalModalOpen={setIsKeywordRemovalModalOpen}
                isKeywordListPopoverOpen={isKeywordListPopoverOpen}
                setIsKeywordListPopoverOpen={setIsKeywordListPopoverOpen}
                keywordListButtonRef={keywordListButtonRef}
                onRemoveKeywordsFromList={onRemoveKeywordsFromList}
            />
            <KeywordGeneratorToolPageModal
                isOpen={isNavigateToGroupModalOpen}
                onCloseClick={() => setIsNavigateToGroupModalOpen(false)}
                onViewGroupClick={onViewGroupClick}
                onStartOverClick={onStartOverClick}
                newlyCreatedGroup={newlyCreatedGroup}
            />
        </>
    );
};
